/* eslint-disable react/no-multi-comp */
import React, {Component} from 'react';
import moment from 'moment';
import 'moment/locale/sv';
import classNames from 'classnames';
import strings from 'utils/strings';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faPrint} from '@fortawesome/free-solid-svg-icons';

const NOT_AVAILABLE = 'Inte tillgänglig';

const get = (object, path, fallback) => {
	if (!object || !path) {
		return fallback;
	}

	const value = path.split('.').reduce((current, key) => {
		if (current && Object.prototype.hasOwnProperty.call(current, key)) {
			return current[key];
		}

		return undefined;
	}, object);

	return value === undefined || value === null ? fallback : value;
};

const hasValue = (value) => {
	return value !== undefined && value !== null && value !== '';
};

const getContent = (props) => {
	if (hasValue(props.children)) {
		return props.children;
	}

	if (hasValue(props.value)) {
		return props.value;
	}

	return NOT_AVAILABLE;
};

const formatDate = (date) => {
	if (!date) {
		return NOT_AVAILABLE;
	}

	const parsedDate = moment(date);

	if (!parsedDate.isValid()) {
		return NOT_AVAILABLE;
	}

	return parsedDate.format('LL');
};

const formatAge = (age) => {
	const years = get(age, 'years');
	const months = get(age, 'months');

	if (!hasValue(years) && !hasValue(months)) {
		return NOT_AVAILABLE;
	}

	if (hasValue(years) && hasValue(months)) {
		return `${years} år och ${months} månader`;
	}

	if (hasValue(years)) {
		return `${years} år`;
	}

	return `${months} månader`;
};

const formatGender = (gender, age) => {
	const years = get(age, 'years');
	const isChild = hasValue(years) && Number(years) < 18;

	if (gender === 'm') {
		return isChild ? strings.boy : strings.male;
	}

	if (gender === 'f') {
		return isChild ? strings.girl : strings.female;
	}

	if (gender === 'u') {
		return 'Ospecificerat';
	}

	return NOT_AVAILABLE;
};

const formatClassGrade = (classValue, education) => {
	const value = hasValue(classValue)
		? String(classValue)
		: hasValue(get(education, 'youthGrade'))
			? String(get(education, 'youthGrade'))
			: '';

	switch (value) {
		case '0':
			return 'Sista året före skolstart';
		case '1':
			return 'Årskurs 1';
		case '2':
			return 'Årskurs 2';
		case '3':
			return 'Årskurs 3';
		case '4':
			return 'Årskurs 4';
		case '5':
			return 'Årskurs 5';
		case '6':
			return 'Årskurs 6';
		case '7':
			return 'Årskurs 7';
		case '8':
			return 'Årskurs 8';
		case '9':
			return 'Årskurs 9';
		case '10':
			return 'Årskurs 10';
		case 'studiespesialisering':
			return 'Studieförberedande program';
		case 'yrkesfag':
			return 'Yrkesprogram';
		case 'bachelor':
			return 'Kandidatexamen';
		case 'master':
			return 'Masterexamen';
		case 'phd':
			return 'Doktorsexamen (PhD)';
		default:
			return NOT_AVAILABLE;
	}
};

const formatLanguagesInHome = (languagesInHome) => {
	switch (languagesInHome) {
		case 'one':
			return 'Ett språk';
		case 'two':
			return 'Två språk';
		case 'multiple':
			return 'Flera språk';
		default:
			return NOT_AVAILABLE;
	}
};

const InfoRow = (props) => {
	// eslint-disable-next-line no-undefined
	if (props.condition !== undefined && !props.condition) {
		return null;
	}

	return (
		<p className="info__row">
			<span className="info__label">{props.name}</span>
			<span className="info__value">{getContent(props)}</span>
		</p>
	);
};

class TestUserInfo extends Component {
	render() {
		const testResult = this.props.testResult || {};

		/*
			Normally the data is expected as:
			testResult.testUser

			This fallback also allows the component to survive if it receives
			a partial user object directly, for example:
			{ education: { youthGrade: 6 } }
		*/
		const testUser = testResult.testUser || testResult || {};

		const isTestOwner = !!testResult.isTestOwner;

		const firstName = get(testUser, 'firstName', '');
		const lastName = get(testUser, 'lastName', '');
		const birthdate = get(testUser, 'birthdate');
		const completedAt = get(testResult, 'completedAt');
		const age = get(testUser, 'age', {});
		const gender = get(testUser, 'gender');
		const education = get(testUser, 'education', {});

		const completeName = firstName
			? `${firstName} ${lastName || ''}`.trim()
			: '<dolt namn>';

		return (
			<div>
				<div className="lit-input-information">
					<button
						className={classNames('print-hide', {
							'lit-btn2 see-more': true,
							'bg-tertiary': true,
						})}
						onClick={() => {
							window.print();
						}}
					>
						<FontAwesomeIcon
							size="lg"
							className="lit-icon-btns"
							icon={faPrint}
						/>

						<p className="lit-btns-txt">
							{strings.printInformation}
						</p>
					</button>
				</div>

				<div className="info">
					<InfoRow
						condition={isTestOwner}
						name={`Namn: `}
						value={completeName}
					/>

					<InfoRow
						condition={isTestOwner}
						name={strings.dateofbirth}
						value={formatDate(birthdate)}
					/>

					<InfoRow
						condition={hasValue(completedAt)}
						name={strings.testdate}
						value={formatDate(completedAt)}
					/>

					<InfoRow name={strings.age} value={formatAge(age)} />

					<InfoRow
						name={strings.sex}
						value={formatGender(gender, age)}
					/>

					<div className="info__group info__group--education">
						<h3>{strings.education}</h3>
					</div>

					<InfoRow
						condition={isTestOwner}
						name={`Region: `}
						value={get(testUser, 'county', NOT_AVAILABLE)}
					/>

					<InfoRow
						condition={isTestOwner}
						name={`Kommun: `}
						value={get(testUser, 'municipality', NOT_AVAILABLE)}
					/>

					<InfoRow
						condition={isTestOwner}
						name={`Skola: `}
						value={get(testUser, 'school', NOT_AVAILABLE)}
					/>

					<InfoRow
						name={`${strings.classGrade}: `}
						value={formatClassGrade(
							get(testUser, 'class'),
							education
						)}
					/>

					<div className="info__group">
						<h3>{strings.homeLanugage}</h3>

						<InfoRow
							name={`Växt upp i ett- eller tvåspråkigt hem? `}
							value={formatLanguagesInHome(
								get(testUser, 'languagesInHome')
							)}
						/>
					</div>
				</div>
			</div>
		);
	}
}

export default TestUserInfo;
